import { Badge, Button, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { TakingAPaymentBack } from '../../components/TakingAPaymentBack';
import { formatMoney } from '../../lib/format';

/**
 * WHAT WAS ACTUALLY COUNTED INTO A DRIVER'S HAND, and taking one back.
 *
 * The payouts list above shows an envelope and a running total, so two hand-overs of one partner's
 * commission read as one payment and a payment made against the wrong partner is invisible unless
 * somebody notices the total. It also had no undo at all: money counted out in error stayed counted
 * out, the drawer's expectation stayed down by it, and the only remedy was to hand the partner less
 * next month and remember why.
 *
 * The same door as a supplier's bill, deliberately — see `components/TakingAPaymentBack.tsx` and
 * `backend/src/domains/cash/reversal.ts`. Rows that were taken back stay on the list beside the
 * negative row that took them, because the person on the other end of this money is standing in the
 * bar being told what he has been given.
 */

const PAYMENTS_PATH = '/admin/v1/referrals/payout-payments';
const LINES_PATH = '/admin/v1/referrals/payout-lines';
const BALANCES_PATH = '/admin/v1/referrals/balances';

interface PayoutPaymentRow {
  id: string;
  partnerName: string | null;
  amountMinor: number;
  currency: string;
  methodUsed: string;
  paidAt: string;
  paidByName: string | null;
  reference: string | null;
  reversalOfPaymentId: string | null;
  reversalReason: string | null;
  reversedByPaymentId: string | null;
}

const asRow = (r: Record<string, unknown>) => r as unknown as PayoutPaymentRow;

export function PayoutPaymentsSection() {
  const queryClient = useQueryClient();
  const [undoing, setUndoing] = useState<PayoutPaymentRow | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(PAYMENTS_PATH) });
    // Taking a payment back makes the partner owed it again, on both screens that say so.
    void queryClient.invalidateQueries({ queryKey: listKey(LINES_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(BALANCES_PATH) });
  };

  return (
    <>
      <CrudSection
        title="Commission handed over"
        description="Every payment counted out against a payout, newest first — including ones that have been taken back. What a payout has been paid is the sum of its rows."
        listPath={PAYMENTS_PATH}
        emptyMessage="Nothing has been handed over yet."
        extractList={(d) =>
          (
            d as {
              payment: Record<string, unknown>;
              partnerName: string | null;
              reversedByPaymentId: string | null;
              paidByName: string | null;
            }[]
          ).map((x) => ({
            ...x.payment,
            partnerName: x.partnerName,
            reversedByPaymentId: x.reversedByPaymentId,
            paidByName: x.paidByName,
          }))
        }
        rowBackgroundColor={(row) =>
          asRow(row).reversedByPaymentId || asRow(row).reversalOfPaymentId
            ? 'var(--mantine-color-gray-light)'
            : undefined
        }
        columns={[
          { accessor: 'paidAt', title: 'When', unit: 'datetime', noWrap: true },
          {
            accessor: 'partnerName',
            title: 'Partner',
            render: (r) => asRow(r).partnerName ?? '—',
          },
          {
            accessor: 'amountMinor',
            title: 'Amount',
            noWrap: true,
            render: (r) => {
              const row = asRow(r);
              const taken = row.amountMinor < 0;
              return (
                <Stack gap={0} align="flex-start">
                  <Text size="sm" fw={600} c={taken ? 'red.7' : undefined}>
                    {formatMoney(row.amountMinor, row.currency)}
                  </Text>
                  {taken && (
                    <Badge size="xs" color="red" variant="light">
                      Taken back
                    </Badge>
                  )}
                  {row.reversedByPaymentId && (
                    <Badge size="xs" color="gray" variant="light">
                      Undone
                    </Badge>
                  )}
                </Stack>
              );
            },
          },
          { accessor: 'methodUsed', title: 'How', enumGroup: 'payoutMethod' },
          {
            accessor: 'reference',
            title: 'Note',
            render: (r) => (
              <Text size="xs" c="dimmed">
                {asRow(r).reversalReason ?? asRow(r).reference ?? ''}
              </Text>
            ),
          },
          { accessor: 'paidByName', title: 'By', render: (r) => asRow(r).paidByName ?? '—' },
        ]}
        rowActions={(row) => {
          const payment = asRow(row);
          if (payment.reversalOfPaymentId || payment.reversedByPaymentId) return null;
          return (
            <Button size="xs" variant="light" color="yellow" onClick={() => setUndoing(payment)}>
              Take it back
            </Button>
          );
        }}
      />

      {undoing && (
        <TakingAPaymentBack
          path={`${PAYMENTS_PATH}/${undoing.id}/reverse`}
          subject={undoing.partnerName ?? 'this partner'}
          amountMinor={undoing.amountMinor}
          currency={undoing.currency}
          consequence="They are owed it again."
          onClose={() => setUndoing(null)}
          onDone={refresh}
        />
      )}
    </>
  );
}
